import React, { useState, useEffect } from 'react';
import { WifiOff } from 'lucide-react';
import { AuthProvider } from './components/auth';
import { PMFSurvey } from './components/feedback';
import { OrganizationProvider } from './hooks/useOrganization';
import { FeatureFlagsProvider } from './hooks/useFeatureFlags';
import { ScopeProvider } from './hooks/useScope';
import { ToastProvider } from './hooks/useToast';
import { LoadingProvider } from './hooks/useLoadingState';
import { ThemeProvider } from './hooks/useTheme';
import { GlobalLoadingIndicator } from './components/ui/LoadingIndicator';
import ErrorBoundary from './components/ErrorBoundary';
import SkipLink from './components/ui/SkipLink';
import LiveRegion from './components/ui/LiveRegion';
import AppRouter, { LoadingFallback } from './Router';
import './App.css';

/**
 * OfflineBanner - Shows a reconnect prompt when the browser loses network connectivity.
 */
const OfflineBanner = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (isOnline) return null;

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="fixed top-0 left-0 right-0 z-[2000] flex items-center justify-between gap-3
                 bg-warning-500/95 backdrop-blur-sm px-4 py-2 text-sm text-white"
    >
      <span className="flex items-center gap-2">
        <WifiOff className="h-4 w-4 flex-shrink-0" />
        You're offline. Check your connection.
      </span>
      <button
        onClick={() => window.location.reload()}
        className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded text-xs font-medium transition-colors"
      >
        Reconnect
      </button>
    </div>
  );
};

// Main App Content (inside AuthProvider)
const AppContent = () => {
  const [showLogin, setShowLogin] = useState(false);
  const [portfolioData, setPortfolioData] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);

  // Load portfolio data with proper cleanup to prevent memory leaks
  useEffect(() => {
    const abortController = new AbortController();

    const loadAnalysisData = async () => {
      try {
        // Try to load the analysis data
        const response = await fetch('/lateral_thinking_analysis.json', {
          signal: abortController.signal
        });
        if (response.ok) {
          const data = await response.json();
          if (!abortController.signal.aborted) {
            setPortfolioData(data);
          }
        }
      } catch (error) {
        // Ignore abort errors
        if (error.name === 'AbortError') {
          return;
        }
        // Analysis data not available, starting with clean state
        // Start with empty data - user will sync their own repositories
        if (!abortController.signal.aborted) {
          setPortfolioData({
            analyzed_projects: []
          });
        }
      } finally {
        if (!abortController.signal.aborted) {
          setDataLoading(false);
        }
      }
    };

    loadAnalysisData();

    // Cleanup function to abort fetch on unmount
    return () => {
      abortController.abort();
    };
  }, []);

  if (dataLoading) {
    return <LoadingFallback />;
  }

  return (
    <AppRouter
      portfolioData={portfolioData}
      onLoginClick={() => setShowLogin(true)}
      showLogin={showLogin}
      setShowLogin={setShowLogin}
    />
  );
};

// Root App Component
function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <LiveRegion>
          <LoadingProvider>
            <OfflineBanner />
            <SkipLink />
            <GlobalLoadingIndicator />
            <ToastProvider>
              <AuthProvider>
                <OrganizationProvider>
                  <FeatureFlagsProvider>
                    <ScopeProvider>
                      <AppContent />
                      <PMFSurvey />
                    </ScopeProvider>
                  </FeatureFlagsProvider>
                </OrganizationProvider>
              </AuthProvider>
            </ToastProvider>
          </LoadingProvider>
        </LiveRegion>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
